// ROUTING IMPORTS
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';

// CONTEXT IMPORTS
import { AuthProvider, useAuth } from './contexts/AuthContext';

// REACT QUERY IMPORTS
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { PromptContextProvider } from './contexts/PromptContext';

// GLOBAL ICON PROVIDER
import { IconoirProvider } from 'iconoir-react';

// SENTRY IMPORTS
import * as Sentry from '@sentry/react';

// GLOBAL NOT FOUND PAGE
import { NotFound } from './NotFound';

const queryClient = new QueryClient();

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { auth: undefined! },
  defaultNotFoundComponent: NotFound,
});

// Register things for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// INITIALIZE SENTRY
if (import.meta.env.VITE_ENV !== 'LOCAL' && import.meta.env.VITE_ENV !== 'staging') {
  Sentry.init({
    dsn: 'https://f25c506117acc5b2fdcae93ed3fee331@o4508558487846912.ingest.us.sentry.io/4508558575992832',
    tracePropagationTargets: [import.meta.env.VITE_CLOUDFUNCTIONS_URI],
    environment: import.meta.env.VITE_ENV,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
      }),
      Sentry.browserTracingIntegration(),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.browserProfilingIntegration(),
      Sentry.feedbackIntegration({
        // Additional SDK configuration goes in here, for example:
        colorScheme: 'system',
      }),
    ],
    // SESSION REPLAY
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // BROWSER TRACING
    tracesSampleRate: 1.0,
    // Setting a sample rate is required for sending performance data.
    // Sentry recommends adjusting this value in production, or using tracesSampler for finer control.

    // BROWSER PROFILING - This feature is in beta and may change in the future. Chrome only as of 1/3/2025
    profilesSampleRate: 1.0,
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // result in 25% of transactions being profiled (0.5*0.5=0.25)
  });
}

function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  return (
    <>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <PromptContextProvider>
            <IconoirProvider
              iconProps={{
                color: 'rgb(3 105 161)',
                strokeWidth: 2,
                width: '2em',
                height: '2em',
              }}
            >
              <InnerApp />
            </IconoirProvider>
          </PromptContextProvider>
        </QueryClientProvider>
      </AuthProvider>
      {/* {import.meta.env.VITE_ENV === 'LOCAL' ||
        (import.meta.env.VITE_ENV === 'staging' && (
          <ReactQueryDevtools initialIsOpen={false} position="bottom" buttonPosition="top-right" />
        ))} */}
    </>
  );
}

export default App;
